import { LogOut, Menu, ShieldCheck, User, X } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button.tsx'
import { useDashboardMode } from '@/features/auth/dashboard-mode.ts'
import { useCurrentUser } from '@/features/user/hooks.ts'
import { signOut } from '@/lib/auth-client.ts'
import { cn } from '@/lib/cn.ts'
import { Logo } from './logo.tsx'
import { navItemsFor } from './nav-items.ts'

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return cn(
    'flex items-center gap-3 rounded-full px-4 py-3 text-label-md font-medium',
    'transition-all duration-300 ease-emphasized active:scale-95',
    isActive
      ? 'bg-secondary-container text-on-secondary-container'
      : 'text-on-surface-variant hover:bg-primary/10 hover:text-on-surface',
  )
}

export function AppShell({ children }: { children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const { data: profile } = useCurrentUser()
  const navigate = useNavigate()
  const location = useLocation()

  const { mode, setMode, canUseAdminMode } = useDashboardMode(profile?.isSuperAdmin ?? false)
  const items = navItemsFor(mode)

  useEffect(() => setMenuOpen(false), [location.pathname])

  function switchMode(next: 'user' | 'admin') {
    setMode(next)
    navigate(next === 'admin' ? '/admin/reports' : '/dashboard')
  }

  return (
    <div className="min-h-screen bg-surface">
      <header className="fixed inset-x-0 top-0 z-40 flex h-16 items-center gap-3 border-b border-outline-variant bg-surface/80 px-4 backdrop-blur-md lg:hidden">
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label="Buka menu"
          className="inline-flex size-10 items-center justify-center rounded-full text-on-surface-variant hover:bg-primary/10"
        >
          <Menu className="size-5" />
        </button>
        <Logo />
      </header>

      {menuOpen ? (
        <button
          type="button"
          aria-label="Tutup menu"
          onClick={() => setMenuOpen(false)}
          className="fixed inset-0 z-40 bg-scrim/30 backdrop-blur-sm lg:hidden"
        />
      ) : null}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-surface-container-low p-4',
          'transition-transform duration-300 ease-emphasized lg:translate-x-0',
          menuOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-12 items-center justify-between px-2">
          <Logo />
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            aria-label="Tutup menu"
            className="inline-flex size-10 items-center justify-center rounded-full text-on-surface-variant hover:bg-primary/10 lg:hidden"
          >
            <X className="size-5" />
          </button>
        </div>

        {canUseAdminMode ? <ModeSwitch mode={mode} onChange={switchMode} /> : null}

        <nav className="mt-4 flex-1 space-y-1 overflow-y-auto">
          {items.map((item) => (
            <NavLink key={item.to} to={item.to} className={navLinkClass}>
              <item.icon className="size-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-outline-variant pt-3">
          {profile ? (
            <div className="mb-2 flex items-center gap-3 px-4 py-2">
              {profile.avatarUrl ? (
                <img src={profile.avatarUrl} alt="" className="size-9 rounded-full object-cover" />
              ) : (
                <span className="inline-flex size-9 items-center justify-center rounded-full bg-primary-container">
                  <User className="size-4 text-on-primary-container" />
                </span>
              )}
              <span className="min-w-0">
                <span className="block truncate text-label-md font-medium text-on-surface">
                  {profile.name}
                </span>
                <span className="block truncate text-label-sm text-on-surface-variant">
                  {profile.email}
                </span>
              </span>
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => void signOut().then(() => navigate('/'))}
            className="flex w-full items-center gap-3 rounded-full px-4 py-3 text-label-md font-medium text-on-surface-variant transition-colors hover:bg-primary/10 hover:text-on-surface"
          >
            <LogOut className="size-5" />
            Keluar
          </button>
        </div>
      </aside>

      <main className="min-h-screen pt-16 lg:pl-72 lg:pt-0">
        <div className="mx-auto max-w-5xl space-y-8 p-4 md:p-8">{children}</div>
      </main>
    </div>
  )
}

/**
 * A super admin has an ordinary account as well, so this flips which face of
 * the dashboard they see. It is a view switch, not impersonation — no request
 * is ever made as another user.
 */
function ModeSwitch({
  mode,
  onChange,
}: {
  mode: 'user' | 'admin'
  onChange: (next: 'user' | 'admin') => void
}) {
  return (
    <div className="mt-4 rounded-lg bg-surface-container p-1">
      <div className="grid grid-cols-2 gap-1">
        <ModeButton
          active={mode === 'user'}
          onClick={() => onChange('user')}
          icon={<User className="size-4" />}
          label="User"
        />
        <ModeButton
          active={mode === 'admin'}
          onClick={() => onChange('admin')}
          icon={<ShieldCheck className="size-4" />}
          label="Admin"
        />
      </div>
    </div>
  )
}

function ModeButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean
  onClick: () => void
  icon: ReactNode
  label: string
}) {
  return (
    <Button
      variant={active ? 'filled' : 'text'}
      size="sm"
      onClick={onClick}
      icon={icon}
      aria-pressed={active}
      className="w-full"
    >
      {label}
    </Button>
  )
}
